import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { BiometricService } from 'app/core/auth/biometric.service';
import { isAdminRole, extractUserRole } from 'app/core/auth/role.constants';

@Component({
    selector: 'app-sign-in',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './sign-in.component.html',
    styleUrl: './sign-in.component.scss',
})
export class SignInComponent implements OnInit {
    @ViewChild('passwordInput') passwordInput?: ElementRef;

    credentials = { identity: '', secret: '' };
    isLoading = false;
    errorMessage = '';
    isCheckingAuth = true;
    showPassword: boolean = false;
    hasUsernameParam: boolean = false;

    // Biometric state
    showFingerprintBtn: boolean = false;
    isFingerprintLoading: boolean = false;
    showBiometricPrompt: boolean = false;
    pendingCredentials: { identity: string; secret: string } | null = null;

    constructor(
        private authService: AuthService,
        private biometricService: BiometricService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    async ngOnInit(): Promise<void> {
        const checkUsername = (param: string | null) => {
            if (param) {
                this.credentials.identity = param.trim();
                this.hasUsernameParam = true;
                setTimeout(() => {
                    if (this.passwordInput?.nativeElement) {
                        this.passwordInput.nativeElement.focus();
                    }
                }, 150);
            }
        };

        const usernameParam =
            this.route.snapshot.paramMap.get('username') ||
            this.route.snapshot.queryParamMap.get('username') ||
            this.route.snapshot.queryParamMap.get('code') ||
            this.route.snapshot.queryParamMap.get('user_code');

        checkUsername(usernameParam);

        this.route.queryParams.subscribe((params) => {
            const u = params['username'] || params['code'] || params['user_code'];
            if (u) {
                checkUsername(u);
            }
        });

        const token = localStorage.getItem('a_id');
        const user = this.authService.getCurrentUser();

        // Jika token & user valid → arahkan berdasarkan role
        if (token && user) {
            const dest = this.getRedirectPath(user);
            this.router.navigate([dest], { replaceUrl: true });
            return;
        }

        // Kalau tidak valid
        localStorage.removeItem('a_id');
        localStorage.removeItem('user');
        this.isCheckingAuth = false;

        // Cek apakah fingerprint tersedia dan ada kredensial tersimpan
        if (!this.hasUsernameParam) {
            const available = await this.biometricService.isAvailable();
            if (available) {
                this.showFingerprintBtn = await this.biometricService.hasCredentials();
            }
        }
    }

    async onSignIn(): Promise<void> {
        this.errorMessage = '';

        const identity = (this.credentials.identity || '').trim();
        const secret = (this.credentials.secret || '').trim();

        if (!identity || !secret) {
            this.errorMessage = 'Masukan email/username dan password/PIN';
            return;
        }

        this.isLoading = true;

        // Cek apakah input berupa email (mengandung @ dan .)
        const isEmail = identity.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity);

        try {
            let result: boolean;

            if (isEmail) {
                // Admin login
                result = await this.authService.signIn({
                    email: identity,
                    password: secret,
                });
            } else {
                // Penghuni login
                result = await this.authService.signInAsOccupant({
                    user_code: identity,
                    pin: secret,
                });
            }

            if (result) {
                this.isCheckingAuth = false;

                const available = await this.biometricService.isAvailable();
                const alreadySaved = await this.biometricService.hasCredentials();

                if (available && !alreadySaved) {
                    // Tawaran aktifkan fingerprint
                    this.pendingCredentials = {
                        identity: identity,
                        secret: secret,
                    };
                    this.showBiometricPrompt = true;
                    this.isLoading = false;
                } else {
                    // Langsung redirect
                    const user = this.authService.getCurrentUser();
                    const dest = this.getRedirectPath(user);
                    this.router.navigate([dest], { replaceUrl: true });
                }
            } else {
                this.errorMessage = isEmail
                    ? 'Email atau password salah'
                    : 'Username atau PIN salah';
            }
        } catch (error: any) {
            this.errorMessage =
                error.message || 'Terjadi kesalahan saat login.';
        } finally {
            if (!this.showBiometricPrompt) {
                this.isLoading = false;
            }
        }
    }

    async onFingerprintLogin(): Promise<void> {
        this.errorMessage = '';
        this.isFingerprintLoading = true;

        try {
            const authResult = await this.biometricService.authenticate();

            if (!authResult.success) {
                if (authResult.errorMessage) {
                    this.errorMessage = authResult.errorMessage;
                }
                return;
            }

            const saved = await this.biometricService.getCredentials();
            if (!saved) {
                this.errorMessage = 'Kredensial tidak ditemukan. Silakan login manual.';
                this.showFingerprintBtn = false;
                return;
            }

            this.isLoading = true;
            const savedIdentity = (saved.identity || '').trim();
            const savedSecret = (saved.secret || '').trim();
            const isEmail = savedIdentity.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(savedIdentity);
            let result: boolean;

            if (isEmail) {
                result = await this.authService.signIn({
                    email: savedIdentity,
                    password: savedSecret,
                });
            } else {
                result = await this.authService.signInAsOccupant({
                    user_code: savedIdentity,
                    pin: savedSecret,
                });
            }

            if (result) {
                const user = this.authService.getCurrentUser();
                const dest = this.getRedirectPath(user);
                this.router.navigate([dest], { replaceUrl: true });
            } else {
                this.errorMessage = 'Login gagal. Silakan login manual.';
                await this.biometricService.clearCredentials();
                this.showFingerprintBtn = false;
            }
        } catch (error: any) {
            this.errorMessage = error.message || 'Terjadi kesalahan saat login sidik jari.';
        } finally {
            this.isFingerprintLoading = false;
            this.isLoading = false;
        }
    }

    async onEnableBiometric(): Promise<void> {
        if (!this.pendingCredentials) return;
        await this.biometricService.saveCredentials(
            this.pendingCredentials.identity,
            this.pendingCredentials.secret
        );
        this.showBiometricPrompt = false;
        this.pendingCredentials = null;
        const user = this.authService.getCurrentUser();
        const dest = this.getRedirectPath(user);
        this.router.navigate([dest], { replaceUrl: true });
    }

    async onSkipBiometric(): Promise<void> {
        this.showBiometricPrompt = false;
        this.pendingCredentials = null;
        const user = this.authService.getCurrentUser();
        const dest = this.getRedirectPath(user);
        this.router.navigate([dest], { replaceUrl: true });
    }

    /**
     * Determine redirect path based on user role.
     * Owner/Keeper/Administrator → /dashboard (admin panel)
     * Renter → /user/home (mobile dashboard)
     */
    private getRedirectPath(user: any): string {
        if (!user) return '/sign-in';
        const roleName = extractUserRole(user?.role || user);

        // Owner, Keeper, Administrator → admin modules
        if (isAdminRole(roleName)) {
            return '/dashboard';
        }

        // Renter → user modules
        return '/user/home';
    }
}
